/**
 * Price and promotion changes. A price is never edited: a new version starts at a chosen time
 * (now or later) and replaces the one in force then. Quotes already given keep their price.
 */

import { CirclePlus, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { CustomerSummary } from '../../api/areas/accounts';
import type { FxRate, PriceBook, PriceRule, Promotion, RegistryPrice, TaxRate } from '../../api/areas/commerce';
import type { PriceBookInput, PriceRuleInput, PromotionInput, PublishedPrice, RegistryCostsInput, RegistryPriceInput, TaxRateInput } from '../../api/areas/operator';
import { Callout, Field } from '../../components/blocks';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime, formatMoney } from '../../lib/format';
import { decimalInput, GridCell, GridHeader, GridRow, isDecimal, roundMoney, rowErrors } from '../commerce/PriceGrid';
import { Amount } from '../commerce/shared';
import { useAddonCatalogue, useAddonName } from '../services/shared';
import { useApi } from '../../lib/useApi';
import { useTierLabel, useTiers } from '../accounts/shared';
import { CustomerPicker } from './RegisterDomain';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, selectClass, type DialogBodyProps } from './shared';

const COST_OPERATIONS = ['REGISTER', 'RENEW', 'TRANSFER', 'RESTORE', 'REDEMPTION'] as const;
/**
 * Add-on prices (`ADDON` + its code) are edited only on the TLD page (tab "Giá & dịch vụ"); these
 * general forms offer `ADDON` only when opened for one add-on (a preset).
 */
const REGISTRY_COST_OPERATIONS = [...COST_OPERATIONS, 'ADDON'] as const;
const RULE_OPERATIONS = [...COST_OPERATIONS, 'BACKORDER', 'AUCTION', 'ADDON'] as const;
const offered = <T extends string>(ops: readonly T[], preset?: CostPreset) => ops.filter((o) => o !== 'ADDON' || preset?.operation === 'ADDON');
const TAX_CATEGORIES = ['VAT', 'KCT'] as const;
const RULE_TYPES: PriceRule['rule_type'][] = ['FIXED_PRICE', 'MARKUP_FIXED', 'MARKUP_PERCENT'];

/** `<input type="datetime-local">` value → ISO instant (the browser's time zone), or undefined. */
function instant(local: string): string | undefined {
  return local ? new Date(local).toISOString() : undefined;
}

/** ISO instant → `<input type="datetime-local">` value. */
function localValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function When({ value, onChange, label, hint }: { value: string; onChange: (v: string) => void; label: string; hint?: string }) {
  return (
    <Field label={label} hint={hint}>
      <Input type="datetime-local" value={value} onChange={(e) => onChange(e.target.value)} className="w-60" />
    </Field>
  );
}

function PublishedNote({ r }: { r: PublishedPrice }) {
  const { t } = useTranslation('operator');
  return <Callout tone="ok">{`${t('pricing.published', { version: r.version })} ${formatDateTime(r.effective_from)}`}</Callout>;
}

/** The add-on picker shown when the operation is `ADDON`. */
function AddonField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t, i18n } = useTranslation('operator');
  const addons = useAddonCatalogue();
  return (
    <Field label={t('pricing.addon')}>
      <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)} required>
        <option value="">{t('common.choose')}</option>
        {addons.map((a) => (
          <option key={a.code} value={a.code}>
            {`${i18n.language.startsWith('vi') ? a.name_vi : a.name_en} (${a.code})`}
          </option>
        ))}
      </select>
    </Field>
  );
}

function useTldOptions() {
  const registries = useApi((signal) => api.listRegistries({ signal }), []);
  return registries.status === 'success' ? registries.data.items : [];
}

const ADDON_OPERATIONS = ['REGISTER', 'RENEW', 'TRANSFER', 'RESTORE'] as const;

/** What the registry-cost dialog opens with (the TLD page preselects its TLD, an add-on, …). */
export interface CostPreset {
  tldId?: string;
  operation?: string;
  addonCode?: string;
}

function RegistryPriceForm({ close, changed, preset }: DialogBodyProps & { preset?: CostPreset }) {
  const { t } = useTranslation('operator');
  const registries = useTldOptions();
  const [tld, setTld] = useState(preset?.tldId ?? '');
  const [operation, setOperation] = useState<string>(preset?.operation ?? 'REGISTER');
  const [addon, setAddon] = useState(preset?.addonCode ?? '');
  const addonName = useAddonName();
  const [basis, setBasis] = useState<'FIXED' | 'PERCENT_OF_REGISTRY_COST'>('FIXED');
  const [forOperation, setForOperation] = useState('');
  const [years, setYears] = useState(1);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('VND');
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  // Bulk helper for add-ons: the same price for other TLDs too (still one row per TLD).
  const [alsoTlds, setAlsoTlds] = useState<string[]>([]);
  const w = useWrite(async (b: RegistryPriceInput, opts): Promise<PublishedPrice> => {
    const first = await api.publishRegistryPrice(b, opts);
    for (const [i, other] of alsoTlds.entries()) {
      await api.publishRegistryPrice({ ...b, tld_id: other }, { ...opts, idempotencyKey: opts.idempotencyKey ? `${opts.idempotencyKey}-${i + 1}` : undefined });
    }
    return first;
  });
  // A registry cost is per TLD only (the connection, channel and environment never select a
  // price): every TLD, once, by zone.
  const zones = registries
    .flatMap((r) => r.tlds)
    .filter((z, i, all) => all.findIndex((x) => x.id === z.id) === i)
    .sort((a, b) => a.zone.localeCompare(b.zone));
  const isAddon = operation === 'ADDON';
  const percent = isAddon && basis === 'PERCENT_OF_REGISTRY_COST';
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w
          .run({
            tld_id: tld,
            operation,
            addon_code: isAddon ? addon : null,
            amount_basis: isAddon ? basis : 'FIXED',
            for_operation: isAddon && forOperation ? forOperation : null,
            years,
            amount: amount.trim(),
            currency: currency.trim().toUpperCase(),
            effective_from: instant(from),
            reason: reason.trim(),
          })
          .then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <PublishedNote r={w.state.result} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('pricing.tld')}>
              <select className={selectClass} value={tld} onChange={(e) => setTld(e.target.value)} required disabled={Boolean(preset?.tldId)}>
                <option value="">{t('common.choose')}</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    .{z.zone}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('pricing.operation')}>
              <select className={selectClass} value={operation} onChange={(e) => setOperation(e.target.value)} disabled={Boolean(preset?.operation)}>
                {offered(REGISTRY_COST_OPERATIONS, preset).map((o) => (
                  <option key={o} value={o}>
                    {t(`pricing.operations.${o}`)}
                  </option>
                ))}
              </select>
            </Field>
            {isAddon ? (preset?.addonCode ? <Field label={t('pricing.addon')}><span className="text-sm" title={preset.addonCode}>{addonName(preset.addonCode)}</span></Field> : <AddonField value={addon} onChange={setAddon} />) : null}
            {isAddon ? (
              <Field label={t('pricing.forOperation')} hint={t('pricing.forOperationHint')}>
                <select className={selectClass} value={forOperation} onChange={(e) => setForOperation(e.target.value)}>
                  <option value="">{t('pricing.everyOperation')}</option>
                  {ADDON_OPERATIONS.map((o) => (
                    <option key={o} value={o}>
                      {t(`pricing.operations.${o}`)}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {isAddon ? (
              <Field label={t('pricing.amountBasis')} hint={t(`pricing.amountBasisHint.${basis}`)}>
                <select className={selectClass} value={basis} onChange={(e) => setBasis(e.target.value as typeof basis)}>
                  {(['FIXED', 'PERCENT_OF_REGISTRY_COST'] as const).map((b) => (
                    <option key={b} value={b}>
                      {t(`pricing.amountBases.${b}`)}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {percent ? null : (
              <Field label={t('pricing.years')} hint={isAddon ? t('pricing.addonYearsHint') : undefined}>
                <Input type="number" min={0} max={10} value={years} onChange={(e) => setYears(Number(e.target.value))} className="w-24" />
              </Field>
            )}
            <Field label={percent ? t('pricing.percent') : t('pricing.amount')}>
              <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(',', '.'))} required />
            </Field>
            {percent ? null : (
              <Field label={t('pricing.currency')}>
                <Input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} className="w-24 uppercase" />
              </Field>
            )}
          </div>
          {isAddon && zones.length > 1 ? (
            <Field label={t('pricing.alsoTlds')} hint={t('pricing.alsoTldsHint')}>
              <div className="flex max-h-40 flex-wrap gap-x-4 gap-y-1 overflow-y-auto rounded-md border p-2">
                {zones
                  .filter((z) => z.id !== tld)
                  .map((z) => (
                    <label key={z.id} className="inline-flex items-center gap-1.5 text-sm">
                      <input type="checkbox" checked={alsoTlds.includes(z.id)} onChange={(e) => setAlsoTlds((x) => (e.target.checked ? [...x, z.id] : x.filter((y) => y !== z.id)))} />.{z.zone}
                    </label>
                  ))}
              </div>
            </Field>
          ) : null}
          <When value={from} onChange={setFrom} label={t('pricing.from')} hint={t('pricing.fromHint')} />
          {preset?.operation === 'ADDON' ? null : <p className="text-xs text-muted-foreground">{t('pricing.addonElsewhere')}</p>}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!tld || !amount.trim() || !reason.trim() || (isAddon && !addon)} submit={w.state.phase === 'done' ? undefined : t('pricing.newCost')} onClose={close} />
    </form>
  );
}

const GRID_OPERATIONS = ['REGISTER', 'RENEW', 'TRANSFER', 'RESTORE'] as const;
const COST_GRID = 'sm:grid-cols-[7rem_minmax(0,1.3fr)_minmax(0,1fr)_5rem_minmax(0,7.5rem)]';

/** The latest rate `base → quote` in force now (1 when the same currency). */
function fxRate(rates: FxRate[], base: string, quote: string): number | null {
  if (base === quote) return 1;
  const now = new Date().toISOString();
  const r = rates.filter((x) => x.base_currency === base && x.quote_currency === quote && x.effective_from <= now).sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
  return r ? Number(r.rate) : null;
}

/**
 * "Giá vốn mới" of one TLD: every operation in one grid (empty = keep), one effective date and
 * reason, saved together (`POST /commerce/pricing/registry-prices/batch`). Without `tldId` the
 * TLD picker comes first.
 */
function RegistryCostGridForm({ close, changed, tldId }: DialogBodyProps & { tldId?: string }) {
  const { t, i18n } = useTranslation('operator');
  const registries = useTldOptions();
  const pricing = useApi((signal) => api.getPricing({ history: false }, { signal }), []);
  const [tld, setTld] = useState(tldId ?? '');
  const [years, setYears] = useState(1);
  const [amounts, setAmounts] = useState<Record<string, { amount: string; currency: string }>>({});
  const [all, setAll] = useState({ amount: '', currency: '' });
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const [sent, setSent] = useState<string[]>([]);
  const w = useWrite((b: RegistryCostsInput, opts) => api.publishRegistryCosts(b, opts));
  const zones = registries
    .flatMap((r) => r.tlds)
    .filter((z, i, list) => list.findIndex((x) => x.id === z.id) === i)
    .sort((a, b) => a.zone.localeCompare(b.zone));
  const now = new Date().toISOString();
  const prices = pricing.status === 'success' ? pricing.data.registry_prices.filter((p) => p.tld_id === tld && !p.addon_code && p.operation !== 'ADDON' && p.years === years) : [];
  const rates = pricing.status === 'success' ? pricing.data.fx_rates : [];
  const current = (op: string) => prices.find((p) => p.operation === op && p.effective_from <= now && (!p.effective_until || p.effective_until > now));
  const scheduled = (op: string) => prices.find((p) => p.operation === op && p.effective_from > now);
  const fallbackCurrency = prices.find((p) => current(p.operation) === p)?.currency ?? 'VND';
  const entry = (op: string) => amounts[op] ?? { amount: '', currency: current(op)?.currency ?? fallbackCurrency };
  const pending = GRID_OPERATIONS.filter((op) => entry(op).amount.trim() !== '');
  const valid = pending.every((op) => isDecimal(entry(op).amount) && /^[A-Za-z]{3}$/.test(entry(op).currency.trim()));
  const errors = w.state.phase === 'error' ? rowErrors(w.state.error, sent) : {};
  const done = w.state.phase === 'done';
  const set = (op: string, v: Partial<{ amount: string; currency: string }>) => setAmounts((x) => ({ ...x, [op]: { ...entry(op), ...v } }));
  const since = (iso: string) => new Date(iso).toLocaleDateString(i18n.language.startsWith('vi') ? 'vi-VN' : 'en-GB');
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        const items = pending.map((op) => ({ operation: op, years, amount: entry(op).amount.trim(), currency: entry(op).currency.trim().toUpperCase() }));
        setSent(items.map((i) => i.operation));
        void w.run({ tld_id: tld, items, effective_from: instant(from), reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('pricing.costGrid.saved', { count: sent.length })}</Callout>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            {tldId ? null : (
              <Field label={t('pricing.tld')} className="min-w-48 flex-1 sm:max-w-xs">
                <select className={selectClass} value={tld} onChange={(e) => setTld(e.target.value)} required>
                  <option value="">{t('common.choose')}</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>
                      .{z.zone}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label={t('pricing.costGrid.years')}>
              <select className={`${selectClass} w-28`} value={years} onChange={(e) => setYears(Number(e.target.value))}>
                {Array.from({ length: 10 }, (_, i) => i + 1).map((y) => (
                  <option key={y} value={y}>
                    {t('pricing.costGrid.yearsValue', { count: y })}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {!tld ? (
            <p className="text-sm text-muted-foreground">{t('pricing.costGrid.pickFirst')}</p>
          ) : pricing.status === 'loading' ? (
            <Loading rows={2} />
          ) : (
            <>
              <div className="grid gap-2 rounded-lg border bg-muted/30 p-3" role="group" aria-label={t('pricing.costGrid.all')}>
                <span className="text-xs font-medium text-muted-foreground">{t('pricing.costGrid.all')}</span>
                <div className="flex flex-wrap items-end gap-2">
                  <Input inputMode="decimal" value={all.amount} onChange={(e) => setAll((x) => ({ ...x, amount: decimalInput(e.target.value) }))} placeholder={t('pricing.amount')} aria-label={t('pricing.amount')} className="w-36" />
                  <Input value={all.currency} onChange={(e) => setAll((x) => ({ ...x, currency: e.target.value }))} placeholder={fallbackCurrency} aria-label={t('pricing.currency')} maxLength={3} className="w-20 uppercase" />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAmounts(Object.fromEntries(GRID_OPERATIONS.map((op) => [op, { amount: all.amount, currency: all.currency.trim() || entry(op).currency }])))}
                  >
                    {t('pricing.costGrid.apply')}
                  </Button>
                </div>
              </div>
              <div className="grid gap-2 sm:gap-0">
                <GridHeader template={COST_GRID} cols={[t('pricing.costGrid.op'), t('pricing.costGrid.current'), t('pricing.costGrid.amount'), t('pricing.currency'), t('pricing.costGrid.converted')]} />
                {GRID_OPERATIONS.map((op) => {
                  const c = current(op);
                  const later = scheduled(op);
                  const e = entry(op);
                  const cur = e.currency.trim().toUpperCase();
                  const rate = /^[A-Z]{3}$/.test(cur) ? fxRate(rates, cur, 'VND') : null;
                  const label = t(`pricing.operations.${op}`);
                  return (
                    <GridRow key={op} template={COST_GRID} error={errors[op]} label={label}>
                      <span className="font-medium">{label}</span>
                      <GridCell label={t('pricing.costGrid.current')}>
                        {c ? <Amount amount={c.amount} currency={c.currency} className="font-medium" /> : <span className="text-warning">{t('pricing.costGrid.none')}</span>}
                        {c ? <span className="block text-xs text-muted-foreground">{t('pricing.costGrid.since', { date: since(c.effective_from), version: c.version })}</span> : null}
                        {later ? (
                          <span className="block text-xs text-muted-foreground">
                            ⏱ {formatMoney({ amount: later.amount, currency: later.currency })} · {t('pricing.costGrid.since', { date: since(later.effective_from), version: later.version })}
                          </span>
                        ) : null}
                      </GridCell>
                      <GridCell label={t('pricing.costGrid.amount')}>
                        <Input
                          inputMode="decimal"
                          value={e.amount}
                          onChange={(ev) => set(op, { amount: decimalInput(ev.target.value) })}
                          placeholder={t('pricing.costGrid.keep')}
                          aria-label={`${label} · ${t('pricing.costGrid.amount')}`}
                          aria-invalid={e.amount !== '' && !isDecimal(e.amount)}
                        />
                      </GridCell>
                      <GridCell label={t('pricing.currency')}>
                        <Input value={e.currency} onChange={(ev) => set(op, { currency: ev.target.value })} maxLength={3} className="w-20 uppercase" aria-label={`${label} · ${t('pricing.currency')}`} />
                      </GridCell>
                      <GridCell label={t('pricing.costGrid.converted')} className="sm:text-right">
                        {e.amount && isDecimal(e.amount) ? (
                          rate !== null ? (
                            <Amount amount={roundMoney(Number(e.amount) * rate, 'VND')} currency="VND" className="font-semibold" />
                          ) : (
                            <span className="text-xs text-warning">{t('pricing.costGrid.noFx')}</span>
                          )
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </GridCell>
                    </GridRow>
                  );
                })}
              </div>
              <When value={from} onChange={setFrom} label={t('pricing.from')} hint={t('pricing.fromHint')} />
              <ReasonField value={reason} onChange={setReason} />
              {pending.length === 0 ? <p className="text-xs text-muted-foreground">{t('pricing.costGrid.nothing')}</p> : null}
            </>
          )}
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter
        busy={w.busy}
        disabled={!tld || !pending.length || !valid || !reason.trim()}
        submit={done || !tld ? undefined : pending.length ? t('pricing.costGrid.save', { count: pending.length }) : t('pricing.newCost')}
        onClose={close}
      />
    </form>
  );
}

/**
 * "Giá vốn mới": the registry cost of a TLD for every operation in one grid (pricing page, TLD
 * page). `tldId` preselects the TLD; `preset` with an add-on keeps the one-price form.
 */
export function RegistryPriceButton({ onChanged, tldId, preset, label, variant }: { onChanged: () => void; tldId?: string; preset?: CostPreset; label?: string; variant?: 'default' | 'outline' | 'ghost' }) {
  const { t } = useTranslation('operator');
  if (preset?.operation === 'ADDON')
    return (
      <ActionDialog label={label ?? t('pricing.newCost')} icon={CirclePlus} variant={variant} title={label ?? t('pricing.newCost')} description={t('pricing.addonCostDescription')} wide onChanged={onChanged}>
        {(d) => <RegistryPriceForm {...d} preset={preset} />}
      </ActionDialog>
    );
  return (
    <ActionDialog label={label ?? t('pricing.newCost')} icon={CirclePlus} variant={variant} title={label ?? t('pricing.newCost')} description={t('pricing.costGrid.description')} wide="xl" onChanged={onChanged}>
      {(d) => <RegistryCostGridForm {...d} tldId={tldId ?? preset?.tldId} />}
    </ActionDialog>
  );
}

function EndForm({ close, changed, run }: DialogBodyProps & { run: (b: { effective_until?: string; reason: string }, opts: Parameters<typeof api.endPriceRule>[2]) => Promise<{ effective_until: string }> }) {
  const { t } = useTranslation('operator');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { effective_until?: string; reason: string }, opts) => run(b, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ effective_until: instant(until), reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <Callout tone="ok">{`${t('common.done')} ${formatDateTime(w.state.result.effective_until)}`}</Callout>
      ) : (
        <>
          <When value={until} onChange={setUntil} label={t('pricing.until')} hint={t('pricing.untilHint')} />
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={w.state.phase === 'done' ? undefined : t('pricing.end')} onClose={close} danger />
    </form>
  );
}

/** "End" on one registry price window that is still open. */
export function EndRegistryPriceButton({ price, onChanged }: { price: RegistryPrice; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  if (price.effective_until) return null;
  return (
    <ActionDialog label={t('pricing.end')} variant="ghost" title={`${t('pricing.endTitle')} · .${price.tld}`} description={t('pricing.endDescription')} onChanged={onChanged}>
      {(p) => <EndForm {...p} run={(b, opts) => api.endRegistryPrice(price.id, b, opts)} />}
    </ActionDialog>
  );
}

/** "End" on a price window known by id (an add-on's cost or selling price on the TLD page). */
export function EndPriceWindowButton({ id, kind, open, title, onChanged }: { id: string; kind: 'registry' | 'rule'; open: boolean; title: string; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  if (!open) return null;
  return (
    <ActionDialog label={t('pricing.end')} variant="ghost" title={title} description={t('pricing.endDescription')} onChanged={onChanged}>
      {(p) => <EndForm {...p} run={(b, opts) => (kind === 'registry' ? api.endRegistryPrice(id, b, opts) : api.endPriceRule(id, b, opts))} />}
    </ActionDialog>
  );
}

/** "End" on one price-book rule window that is still open. */
export function EndRuleButton({ rule, onChanged }: { rule: PriceRule; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  if (rule.effective_until) return null;
  return (
    <ActionDialog label={t('pricing.end')} variant="ghost" title={t('pricing.endTitle')} description={t('pricing.endDescription')} onChanged={onChanged}>
      {(p) => <EndForm {...p} run={(b, opts) => api.endPriceRule(rule.id, b, opts)} />}
    </ActionDialog>
  );
}

function BookForm({ close, changed }: DialogBodyProps) {
  const { t } = useTranslation('operator');
  const [code, setCode] = useState('');
  const [currency, setCurrency] = useState('VND');
  const [appliesTo, setAppliesTo] = useState<PriceBook['applies_to']>('TIER');
  const [tier, setTier] = useState('STANDARD');
  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  const [reason, setReason] = useState('');
  const tiers = useTiers();
  const tierLabel = useTierLabel();
  const tierOptions: [string, string][] = tiers.length ? tiers.map((x) => [x.code, tierLabel(x)]) : [['STANDARD', 'STANDARD']];
  const w = useWrite((b: PriceBookInput, opts) => api.createPriceBook(b, opts));
  const contract = appliesTo === 'CUSTOMER_CONTRACT';
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w
          .run({ code: code.trim(), currency: currency.trim().toUpperCase(), applies_to: appliesTo, tier: contract ? null : tier, customer_id: contract ? (customer?.id ?? null) : null, reason: reason.trim() })
          .then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <Callout tone="ok">{`${t('common.done')} ${t('pricing.statusDraft')}`}</Callout>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('pricing.code')}>
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="font-mono" maxLength={64} required />
            </Field>
            <Field label={t('pricing.currency')}>
              <Input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} className="w-24 uppercase" />
            </Field>
            <Field label={t('pricing.appliesTo')}>
              <select className={selectClass} value={appliesTo} onChange={(e) => setAppliesTo(e.target.value as PriceBook['applies_to'])}>
                {(['TIER', 'CUSTOMER_CONTRACT'] as const).map((a) => (
                  <option key={a} value={a}>
                    {t(`pricing.appliesToValues.${a}`)}
                  </option>
                ))}
              </select>
            </Field>
            {contract ? (
              <Field label={t('pricing.customer')} group>
                <CustomerPicker value={customer} onChange={setCustomer} />
              </Field>
            ) : (
              <Field label={t('pricing.tier')} hint={t('pricing.tierHint')}>
                <select className={selectClass} value={tier} onChange={(e) => setTier(e.target.value)}>
                  {tierOptions.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!code.trim() || (contract ? !customer : !tier) || !reason.trim()} submit={w.state.phase === 'done' ? undefined : t('pricing.newBook')} onClose={close} />
    </form>
  );
}

/** "New price book" (pricing page). */
export function NewPriceBookButton({ onChanged }: { onChanged: () => void }) {
  const { t } = useTranslation('operator');
  return (
    <ActionDialog label={t('pricing.newBook')} icon={CirclePlus} title={t('pricing.newBook')} description={t('pricing.newBookDescription')} wide onChanged={onChanged}>
      {(p) => <BookForm {...p} />}
    </ActionDialog>
  );
}

/** "Make active" / "Retire" on a price book. */
export function PriceBookStatusButtons({ book, onChanged }: { book: PriceBook; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  const run = (status: PriceBook['status']) => (reason: string, opts: Parameters<typeof api.setPriceBookStatus>[2]) => api.setPriceBookStatus(book.id, { status, reason }, opts);
  return (
    <>
      {book.status === 'DRAFT' ? (
        <ReasonAction label={t('pricing.activate')} title={`${t('pricing.statusTitle')} · ${book.code}`} run={run('ACTIVE')} done={() => t('pricing.statusActive')} onChanged={onChanged} />
      ) : null}
      {book.status !== 'RETIRED' ? (
        <ReasonAction
          label={t('pricing.retire')}
          title={`${t('pricing.statusTitle')} · ${book.code}`}
          warning={t('pricing.retiredForever')}
          danger
          run={run('RETIRED')}
          done={() => t('pricing.statusRetired')}
          onChanged={onChanged}
        />
      ) : null}
    </>
  );
}

function RuleForm({ close, changed, book, tldId, preset }: DialogBodyProps & { book: PriceBook; tldId?: string; preset?: CostPreset }) {
  const { t } = useTranslation('operator');
  const registries = useTldOptions();
  const zones = registries
    .flatMap((r) => r.tlds)
    .filter((z, i, all) => all.findIndex((x) => x.id === z.id) === i)
    .sort((a, b) => a.zone.localeCompare(b.zone));
  const [tld, setTld] = useState(preset?.tldId ?? tldId ?? '');
  const [operation, setOperation] = useState<string>(preset?.operation ?? 'REGISTER');
  const [addon, setAddon] = useState(preset?.addonCode ?? '');
  const addonName = useAddonName();
  const [taxCategory, setTaxCategory] = useState<string>('VAT');
  const [taxCode, setTaxCode] = useState('');
  const [years, setYears] = useState('');
  const [ruleType, setRuleType] = useState<PriceRule['rule_type']>('FIXED_PRICE');
  const [value, setValue] = useState('');
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [priority, setPriority] = useState(100);
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const w = useWrite((b: PriceRuleInput, opts) => api.publishPriceRule(book.id, b, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w
          .run({
            tld_id: tld || null,
            operation,
            addon_code: operation === 'ADDON' ? addon : null,
            tax_category: taxCategory as 'VAT' | 'KCT',
            tax_code: taxCategory === 'VAT' ? taxCode.trim() || null : null,
            years: years ? Number(years) : null,
            rule_type: ruleType,
            value: value.trim(),
            min_amount: min.trim() || null,
            max_amount: max.trim() || null,
            priority,
            effective_from: instant(from),
            reason: reason.trim(),
          })
          .then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <PublishedNote r={w.state.result} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('pricing.tld')} hint={operation === 'ADDON' ? t('pricing.addonPerTld') : undefined}>
              <select className={selectClass} value={tld} onChange={(e) => setTld(e.target.value)} disabled={Boolean(preset?.tldId)} required={operation === 'ADDON'}>
                <option value="">{operation === 'ADDON' ? t('common.choose') : t('pricing.anyTld')}</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    .{z.zone}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('pricing.operation')}>
              <select className={selectClass} value={operation} onChange={(e) => setOperation(e.target.value)} disabled={Boolean(preset?.operation)}>
                {offered(RULE_OPERATIONS, preset).map((o) => (
                  <option key={o} value={o}>
                    {t(`pricing.operations.${o}`)}
                  </option>
                ))}
              </select>
            </Field>
            {operation === 'ADDON' ? (preset?.addonCode ? <Field label={t('pricing.addon')}><span className="text-sm" title={preset.addonCode}>{addonName(preset.addonCode)}</span></Field> : <AddonField value={addon} onChange={setAddon} />) : null}
            <Field label={t('pricing.years')}>
              <select className={selectClass} value={years} onChange={(e) => setYears(e.target.value)}>
                <option value="">{t('pricing.anyYears')}</option>
                {[1, 2, 3, 5, 10].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('pricing.ruleType')}>
              <select className={selectClass} value={ruleType} onChange={(e) => setRuleType(e.target.value as PriceRule['rule_type'])}>
                {RULE_TYPES.map((r) => (
                  <option key={r} value={r}>
                    {t(`pricing.ruleTypes.${r}`)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={`${t('pricing.value')} (${ruleType === 'MARKUP_PERCENT' ? '%' : book.currency})`} hint={t('pricing.valueHint')}>
              <Input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value.replace(',', '.'))} required />
            </Field>
            <Field label={t('pricing.priority')} hint={t('pricing.priorityHint')}>
              <Input type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} className="w-24" />
            </Field>
            <Field label={t('pricing.taxCategory')}>
              <select className={selectClass} value={taxCategory} onChange={(e) => setTaxCategory(e.target.value)}>
                {TAX_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`pricing.taxCategories.${c}`)}
                  </option>
                ))}
              </select>
            </Field>
            {taxCategory === 'VAT' ? (
              <Field label={`${t('pricing.taxCode')} (${t('common.optional')})`} hint={t('pricing.taxCodeHint')}>
                <Input value={taxCode} onChange={(e) => setTaxCode(e.target.value)} />
              </Field>
            ) : null}
            <Field label={`${t('pricing.min')} (${t('common.optional')})`}>
              <Input inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value.replace(',', '.'))} />
            </Field>
            <Field label={`${t('pricing.max')} (${t('common.optional')})`}>
              <Input inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value.replace(',', '.'))} />
            </Field>
          </div>
          <When value={from} onChange={setFrom} label={t('pricing.from')} hint={t('pricing.fromHint')} />
          {preset?.operation === 'ADDON' ? null : <p className="text-xs text-muted-foreground">{t('pricing.addonElsewhere')}</p>}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!value.trim() || !reason.trim() || (operation === 'ADDON' && (!addon || !tld))} submit={w.state.phase === 'done' ? undefined : t('pricing.newRule')} onClose={close} />
    </form>
  );
}

/** "Selling price…" for a preset (an add-on on a TLD): choose the price book, then the rule. */
export function PresetRuleButton({ books, preset, label, onChanged }: { books: PriceBook[]; preset: CostPreset; label: string; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  const usable = books.filter((b) => b.status !== 'RETIRED');
  if (!usable.length) return null;
  return (
    <ActionDialog label={label} icon={CirclePlus} variant="ghost" title={label} description={t('pricing.newRuleDescription')} wide onChanged={onChanged}>
      {(p) => <BookThenRule {...p} books={usable} preset={preset} />}
    </ActionDialog>
  );
}

function BookThenRule({ close, changed, books, preset }: DialogBodyProps & { books: PriceBook[]; preset: CostPreset }) {
  const { t } = useTranslation('operator');
  const [bookId, setBookId] = useState(books[0]?.id ?? '');
  const book = books.find((b) => b.id === bookId);
  return (
    <div className="grid gap-4">
      <Field label={t('pricing.book')}>
        <select className={selectClass} value={bookId} onChange={(e) => setBookId(e.target.value)}>
          {books.map((b) => (
            <option key={b.id} value={b.id}>
              {b.code} ({b.currency})
            </option>
          ))}
        </select>
      </Field>
      {book ? <RuleForm key={book.id} close={close} changed={changed} book={book} preset={preset} /> : null}
    </div>
  );
}

/** "New selling price" on a price book. */
export function PriceRuleButton({ book, onChanged, tldId, preset, label, variant }: { book: PriceBook; onChanged: () => void; tldId?: string; preset?: CostPreset; label?: string; variant?: 'default' | 'outline' | 'ghost' }) {
  const { t } = useTranslation('operator');
  if (book.status === 'RETIRED') return null;
  return (
    <ActionDialog label={label ?? t('pricing.newRule')} icon={CirclePlus} variant={variant} title={`${label ?? t('pricing.newRule')} · ${book.code}`} description={t('pricing.newRuleDescription')} wide onChanged={onChanged}>
      {(p) => <RuleForm {...p} book={book} tldId={tldId} preset={preset} />}
    </ActionDialog>
  );
}

// ---------------------------------------------------------------------------
// Promotions

function PromotionForm({ close, changed, promotion }: DialogBodyProps & { promotion?: Promotion }) {
  const { t } = useTranslation('operator');
  const registries = useTldOptions();
  const zones = registries.flatMap((r) => r.tlds).sort((a, b) => a.zone.localeCompare(b.zone));
  const used = (promotion?.redemptions ?? 0) > 0;
  const [code, setCode] = useState(promotion?.code ?? '');
  const [type, setType] = useState<'PERCENT' | 'FIXED'>(promotion?.discount_type ?? 'PERCENT');
  const [value, setValue] = useState(promotion?.value ?? '');
  const [currency, setCurrency] = useState(promotion?.currency ?? 'VND');
  const [tld, setTld] = useState(promotion?.tld_id ?? '');
  const [operation, setOperation] = useState(promotion?.operation ?? 'REGISTER');
  const [starts, setStarts] = useState(localValue(promotion?.starts_at));
  const [ends, setEnds] = useState(localValue(promotion?.ends_at));
  const [max, setMax] = useState(promotion?.max_redemptions?.toString() ?? '');
  const [reason, setReason] = useState('');
  const w = useWrite((b: PromotionInput, opts): Promise<unknown> => (promotion ? api.updatePromotion(promotion.id, b, opts) : api.createPromotion(b, opts)));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        const body: PromotionInput = {
          discount_type: type,
          value: value.trim(),
          currency: type === 'FIXED' ? currency.trim().toUpperCase() : null,
          tld_id: tld || null,
          operation: operation || null,
          starts_at: instant(starts) ?? promotion?.starts_at,
          ends_at: instant(ends) ?? '',
          max_redemptions: max.trim() ? Number(max) : null,
          reason: reason.trim(),
          ...(promotion ? { version: promotion.version ?? 0 } : { code: code.trim().toUpperCase() }),
        };
        void w.run(body).then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <Callout tone="ok">{promotion ? t('promotions.saved') : t('promotions.created', { code: code.trim().toUpperCase() })}</Callout>
      ) : (
        <>
          {promotion && used ? <Callout tone="info">{t('promotions.editDescription')}</Callout> : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('promotions.code')} hint={promotion ? undefined : t('promotions.codeHint')}>
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="font-mono" disabled={Boolean(promotion)} required />
            </Field>
            <Field label={t('promotions.discountType')}>
              <select className={selectClass} value={type} onChange={(e) => setType(e.target.value as 'PERCENT' | 'FIXED')} disabled={used}>
                <option value="PERCENT">{t('promotions.percent')}</option>
                <option value="FIXED">{t('promotions.fixed')}</option>
              </select>
            </Field>
            <Field label={`${t('promotions.value')} (${type === 'PERCENT' ? '%' : currency})`}>
              <Input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value.replace(',', '.'))} disabled={used} required />
            </Field>
            {type === 'FIXED' ? (
              <Field label={t('promotions.currency')}>
                <Input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} className="w-24 uppercase" disabled={used} />
              </Field>
            ) : null}
            <Field label={t('promotions.tld')}>
              <select className={selectClass} value={tld} onChange={(e) => setTld(e.target.value)} disabled={used}>
                <option value="">{t('promotions.anyTld')}</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    .{z.zone}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('promotions.operation')}>
              <select className={selectClass} value={operation} onChange={(e) => setOperation(e.target.value)} disabled={used}>
                <option value="">{t('promotions.anyOperation')}</option>
                {COST_OPERATIONS.map((o) => (
                  <option key={o} value={o}>
                    {t(`pricing.operations.${o}`)}
                  </option>
                ))}
              </select>
            </Field>
            <When value={starts} onChange={setStarts} label={t('promotions.starts')} />
            <When value={ends} onChange={setEnds} label={t('promotions.ends')} />
            <Field label={`${t('promotions.maxUses')} (${t('common.optional')})`} hint={t('promotions.maxUsesHint')}>
              <Input type="number" min={1} value={max} onChange={(e) => setMax(e.target.value)} className="w-32" />
            </Field>
          </div>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter
        busy={w.busy}
        disabled={!code.trim() || !value.trim() || !ends || !reason.trim()}
        submit={w.state.phase === 'done' ? undefined : promotion ? t('promotions.edit') : t('promotions.create')}
        onClose={close}
      />
    </form>
  );
}

/** "New promotion" (promotions page). */
export function NewPromotionButton({ onChanged }: { onChanged: () => void }) {
  const { t } = useTranslation('operator');
  return (
    <ActionDialog label={t('promotions.create')} icon={CirclePlus} title={t('promotions.create')} description={t('promotions.createDescription')} variant="default" wide onChanged={onChanged}>
      {(p) => <PromotionForm {...p} />}
    </ActionDialog>
  );
}

/** "Edit" and "Disable" on one promotion. */
export function PromotionActions({ promotion, onChanged }: { promotion: Promotion; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  if (promotion.disabled_at) return null;
  return (
    <>
      <ActionDialog label={t('promotions.edit')} icon={Pencil} title={`${t('promotions.edit')} · ${promotion.code}`} description={t('promotions.editDescription')} wide onChanged={onChanged}>
        {(p) => <PromotionForm {...p} promotion={promotion} />}
      </ActionDialog>
      <ReasonAction
        label={t('promotions.disable')}
        title={`${t('promotions.disableTitle')} · ${promotion.code}`}
        warning={t('promotions.disableDescription')}
        danger
        run={(reason, opts) => api.disablePromotion(promotion.id, { reason }, opts)}
        done={() => t('promotions.disabled')}
        onChanged={onChanged}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// VAT rates (`tax.manage`; owner decision 2026-10-05: the rate is chosen per product, 10% today)

function TaxRateForm({ close, changed, code: presetCode }: DialogBodyProps & { code?: string }) {
  const { t } = useTranslation('operator');
  const [code, setCode] = useState(presetCode ?? 'VAT10');
  const [percent, setPercent] = useState('10');
  const [source, setSource] = useState('');
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const w = useWrite((b: TaxRateInput, opts) => api.publishTaxRate(b, opts));
  const valid = /^[A-Z0-9_]{1,32}$/.test(code) && code !== 'KCT' && isDecimal(percent) && Number(percent) >= 0 && Number(percent) < 100;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ tax_code: code, rate_percent: percent.trim(), source_reference: source.trim(), effective_from: instant(from), reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <PublishedNote r={w.state.result} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('tax.code')} hint={t('tax.codeHint')}>
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="font-mono" maxLength={32} required disabled={Boolean(presetCode)} />
            </Field>
            <Field label={t('tax.percent')}>
              <Input value={percent} onChange={(e) => setPercent(decimalInput(e.target.value))} inputMode="decimal" className="w-28 tabular" required />
            </Field>
          </div>
          <Field label={t('tax.source')} hint={t('tax.sourceHint')}>
            <Input value={source} onChange={(e) => setSource(e.target.value)} maxLength={300} required />
          </Field>
          <When value={from} onChange={setFrom} label={t('pricing.from')} hint={t('pricing.fromHint')} />
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!valid || !source.trim() || !reason.trim()} submit={w.state.phase === 'done' ? undefined : t('tax.publish')} onClose={close} />
    </form>
  );
}

/** "New VAT rate", or a new version of one code. */
export function TaxRateButton({ onChanged, code, variant }: { onChanged: () => void; code?: string; variant?: 'default' | 'outline' | 'ghost' }) {
  const { t } = useTranslation('operator');
  const label = code ? t('tax.newVersion') : t('tax.new');
  return (
    <ActionDialog label={label} icon={code ? Pencil : CirclePlus} variant={variant ?? (code ? 'ghost' : 'default')} title={code ? `${t('tax.newVersion')} · ${code}` : t('tax.new')} description={t('tax.description')} wide onChanged={onChanged}>
      {(d) => <TaxRateForm {...d} code={code} />}
    </ActionDialog>
  );
}

/** "End" on a VAT rate version that is still open. */
export function EndTaxRateButton({ rate, onChanged }: { rate: TaxRate; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  if (rate.effective_until) return null;
  return (
    <ActionDialog label={t('pricing.end')} variant="ghost" title={`${t('tax.endTitle')} · ${rate.tax_code}`} description={t('tax.endDescription')} onChanged={onChanged}>
      {(p) => <EndForm {...p} run={(b, opts) => api.endTaxRate(rate.id, b, opts)} />}
    </ActionDialog>
  );
}

/** "Make default": the VAT code of TLD and add-on prices that name none. */
export function TaxDefaultButton({ code, current, onChanged }: { code: string; current: string | null | undefined; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  if (current === code) return null;
  return (
    <ReasonAction
      label={t('tax.makeDefault')}
      title={`${t('tax.makeDefault')} · ${code}`}
      description={t('tax.defaultDescription', { code })}
      variant="ghost"
      run={(reason, opts) => api.setTaxDefault({ tax_code: code, reason }, opts)}
      done={(r) => t('tax.defaultSet', { code: r.tax_code })}
      onChanged={onChanged}
    />
  );
}
